import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2, Calendar, MapPin, Monitor, Clock, ShieldCheck, Handshake, TrendingUp, Star, Award,
  Globe, GraduationCap, Target, Rocket, Users, MessageCircle, DollarSign, ArrowRight, Ticket,
} from 'lucide-react';
import api from '../../lib/api';
import ShareMenu from '../../components/ShareMenu';

const ICONS = { ShieldCheck, Handshake, TrendingUp, Star, Award, Globe, GraduationCap, Target, Rocket, Users };
const MODALITY_LABEL = { presencial: 'Presencial', zoom: 'Vía Zoom', hibrido: 'Híbrido' };

function formatRange(start, end) {
  if (!start) return 'Fechas por confirmar';
  try {
    const s = new Date(start);
    const e = end ? new Date(end) : s;
    const sameDay = s.toDateString() === e.toDateString();
    const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
    if (sameDay) return s.toLocaleDateString('es-BO', { day: '2-digit', month: 'long', year: 'numeric' });
    if (sameMonth) return `${s.getDate()} al ${e.getDate()} de ${s.toLocaleDateString('es-BO', { month: 'long', year: 'numeric' })}`;
    return `${s.toLocaleDateString('es-BO', { day: '2-digit', month: 'short' })} — ${e.toLocaleDateString('es-BO', { day: '2-digit', month: 'short', year: 'numeric' })}`;
  } catch { return ''; }
}

function Countdown({ target, color }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(i);
  }, []);
  const diff = Math.max(0, new Date(target).getTime() - Date.now());
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  if (diff === 0) {
    return (
      <p className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold" style={{ background: `${color}20`, color }}>
        ¡El curso ya comenzó!
      </p>
    );
  }
  const box = (n, l) => (
    <div className="flex flex-col items-center">
      <div className="rounded-2xl px-3 py-2 text-center ring-1 ring-white/15 backdrop-blur min-w-[62px]" style={{ background: 'rgba(255,255,255,0.08)' }}>
        <p className="font-heading text-2xl font-black leading-none" style={{ color }}>{String(n).padStart(2, '0')}</p>
      </div>
      <p className="mt-1 text-[9px] font-bold uppercase tracking-wider text-slate-300">{l}</p>
    </div>
  );
  return (
    <div className="flex items-center gap-2" data-testid="training-countdown">
      {box(days, 'Días')}
      {box(hours, 'Horas')}
      {box(mins, 'Min')}
      {box(secs, 'Seg')}
    </div>
  );
}

export default function CursoMicrositio() {
  const { slug } = useParams();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/trainings/${slug}`);
        setT(data);
      } catch {
        setT(null);
      } finally { setLoading(false); }
    })();
  }, [slug]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-900"><Loader2 className="h-10 w-10 animate-spin text-orange-500" /></div>;
  }
  if (!t) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-900 text-white">
        <p className="font-heading text-2xl">Capacitación no encontrada</p>
        <Link to="/capacitacion" className="mt-4 rounded-full bg-orange-500 px-5 py-2 text-sm font-bold">Ver todas</Link>
      </div>
    );
  }

  const primary = t.primary_color || '#F5831F';
  const accent = t.accent_color || '#0F4C81';
  const sponsors = (Array.isArray(t.sponsors) ? t.sponsors : [])
    .map((s) => (typeof s === 'string' ? { name: s } : s))
    .filter((s) => s && (s.name || s.logo));
  const sponsorsByTier = sponsors.reduce((acc, s) => {
    const t2 = s.tier || 'Auspiciadores';
    (acc[t2] = acc[t2] || []).push(s);
    return acc;
  }, {});
  const benefits = Array.isArray(t.benefits) ? t.benefits : [];
  const prices = (Array.isArray(t.prices) ? t.prices : []).filter((p) => p.label);
  const testimonials = Array.isArray(t.testimonials) ? t.testimonials : [];

  const waNumber = (t.whatsapp_number || '').replace(/\D/g, '');
  const waMessage = t.whatsapp_message || `Hola, quiero información sobre la capacitación ${t.title}`;
  const waUrl = waNumber ? `https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}` : null;
  const buyUrl = t.cta_mode === 'payment' && t.cta_url ? t.cta_url : waUrl;
  const buyLabel = t.cta_mode === 'payment' && t.cta_url ? 'Comprar capacitación' : 'Reservar por WhatsApp';

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="training-micrositio">
      {/* HERO */}
      <section className="relative overflow-hidden pb-20 pt-6">
        <div className="absolute inset-0">
          {t.cover_image ? (
            <img src={t.cover_image} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }} />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/50 to-slate-900" />
        </div>
        <div className="relative mx-auto max-w-6xl px-4 pt-28 sm:px-6 md:pt-36">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            {t.eyebrow && <span className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider" style={{ background: `${primary}25`, color: primary }}>{t.eyebrow}</span>}
            <h1 data-testid="training-title" className="mt-4 font-heading text-5xl font-black leading-[0.95] tracking-tight sm:text-6xl md:text-7xl" style={{ color: primary }}>{t.title}</h1>
            {t.subtitle && <p className="mt-3 text-sm font-bold uppercase tracking-wider text-white/80 sm:text-base">{t.subtitle}</p>}

            <div className="mt-7 flex flex-wrap items-center gap-3 text-sm text-white/90">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 backdrop-blur"><Calendar className="h-3.5 w-3.5" style={{ color: primary }} /> {formatRange(t.starts_at, t.ends_at)}</span>
              {t.duration && <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 backdrop-blur"><Clock className="h-3.5 w-3.5" style={{ color: primary }} /> {t.duration}</span>}
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 backdrop-blur"><Monitor className="h-3.5 w-3.5" style={{ color: primary }} /> {MODALITY_LABEL[t.modality] || t.modality}</span>
              {t.city && <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 backdrop-blur"><MapPin className="h-3.5 w-3.5" style={{ color: primary }} /> {t.city}{t.venue ? ` · ${t.venue}` : ''}</span>}
              {t.seats_limited && t.seats_quantity && <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/20 px-3 py-1.5 text-orange-200 backdrop-blur"><Users className="h-3.5 w-3.5" /> Solo {t.seats_quantity} cupos</span>}
            </div>

            {t.starts_at && (
              <div className="mt-8">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.3em] text-white/60">Inicia en</p>
                <Countdown target={t.starts_at} color={primary} />
              </div>
            )}

            <div className="mt-9 flex flex-wrap items-center gap-3">
              {buyUrl && (
                <a href={buyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-white shadow-xl transition hover:-translate-y-0.5" style={{ background: primary }} data-testid="training-buy-cta">
                  <Ticket className="h-4 w-4" /> {buyLabel}
                </a>
              )}
              {waUrl && t.cta_mode === 'payment' && (
                <a href={waUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border-2 border-emerald-400/70 bg-white/5 px-5 py-3 text-sm font-bold text-emerald-300 backdrop-blur hover:bg-emerald-500/10">
                  <MessageCircle className="h-4 w-4" /> Más información por WhatsApp
                </a>
              )}
              <ShareMenu url={typeof window !== 'undefined' ? window.location.href : ''} title={`${t.title} — CNIB`} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS */}
      {benefits.length > 0 && (
        <section className="border-t border-slate-800/60 bg-slate-900 py-16" data-testid="training-benefits">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: primary }}>Lo que te llevas</p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold">Beneficios y ventajas</h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {benefits.map((b, i) => {
                const Icon = ICONS[b.icon] || ShieldCheck;
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }} className="rounded-2xl bg-slate-800/60 p-6 ring-1 ring-white/5">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${primary}20`, color: primary }}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 font-heading text-base font-extrabold uppercase leading-tight">{b.title}</h3>
                    {b.description && <p className="mt-2 text-sm text-slate-400">{b.description}</p>}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* PRICES */}
      {prices.length > 0 && (
        <section className="py-16" style={{ background: `linear-gradient(135deg, ${accent}20, transparent)` }} data-testid="training-prices">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: primary }}>Inversión</p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold">Precios</h2>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {prices.map((p, i) => (
                <div key={i} className="rounded-2xl border border-white/10 bg-slate-900/60 p-6 ring-1 ring-white/5 transition hover:-translate-y-1" data-testid={`training-price-${i}`}>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-white/60">{p.label}</p>
                  <p className="mt-2 font-heading text-4xl font-black" style={{ color: primary }}>US$ {p.amount_usd}</p>
                  {p.note && <p className="mt-2 text-xs text-slate-400">{p.note}</p>}
                </div>
              ))}
            </div>
            {buyUrl && (
              <div className="mt-8 text-center">
                <a href={buyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-bold text-white shadow-xl" style={{ background: primary }}>
                  <DollarSign className="h-4 w-4" /> {buyLabel} <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* DESCRIPTION */}
      {t.description && (
        <section className="border-t border-slate-800/60 py-16" data-testid="training-description">
          <div className="mx-auto max-w-4xl px-4 sm:px-6">
            <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: primary }}>Detalles</p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold">Sobre el programa</h2>
            <div className="prose prose-invert mt-6 max-w-none text-slate-200" dangerouslySetInnerHTML={{ __html: t.description }} />
          </div>
        </section>
      )}

      {/* INSTRUCTOR */}
      {t.instructor?.name && (
        <section className="border-t border-slate-800/60 bg-slate-900/50 py-16" data-testid="training-instructor">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 px-4 text-center sm:px-6 md:flex-row md:text-left">
            {t.instructor.logo && <img src={t.instructor.logo} alt={t.instructor.name} className="h-24 w-auto rounded-2xl bg-white p-3 object-contain" />}
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: primary }}>Avalado por</p>
              <h3 className="mt-2 font-heading text-2xl font-extrabold">{t.instructor.name}</h3>
              {t.instructor.bio && <p className="mt-2 text-sm text-slate-300">{t.instructor.bio}</p>}
              {t.instructor.url && <a href={t.instructor.url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm font-bold" style={{ color: primary }}>Más información <ArrowRight className="h-3.5 w-3.5" /></a>}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="border-t border-slate-800/60 py-16" data-testid="training-testimonials">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: primary }}>Testimonios</p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold">Lo que dicen quienes se certificaron</h2>
            <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {testimonials.map((tst, i) => (
                <div key={i} className="rounded-2xl bg-slate-800/60 p-6 ring-1 ring-white/5">
                  <p className="text-sm italic text-slate-200">“{tst.quote}”</p>
                  <div className="mt-4 flex items-center gap-3">
                    {tst.photo && <img src={tst.photo} alt="" className="h-10 w-10 rounded-full object-cover" />}
                    <div>
                      <p className="text-sm font-bold">{tst.name}</p>
                      {tst.role && <p className="text-xs text-slate-400">{tst.role}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CERTIFICATE SAMPLE */}
      {t.certificate_image && (
        <section className="border-t border-slate-800/60 bg-slate-900/60 py-16" data-testid="training-certificate">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
            <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: primary }}>Certificación</p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold">Recibirás un certificado así</h2>
            <img src={t.certificate_image} alt="Certificado de ejemplo" className="mx-auto mt-8 max-w-full rounded-2xl shadow-2xl" />
          </div>
        </section>
      )}

      {/* SPONSORS */}
      {sponsors.length > 0 && (
        <section className="border-t border-slate-800/60 bg-slate-900/60 py-16" data-testid="training-sponsors">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: primary }}>Con el apoyo de</p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold">Auspiciadores y partners</h2>
            <div className="mt-8 space-y-10">
              {Object.entries(sponsorsByTier).map(([tier, list]) => (
                <div key={tier}>
                  <h3 className="mb-5 text-[11px] font-bold uppercase tracking-[0.25em] text-slate-400">{tier}</h3>
                  <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                    {list.map((s, i) => {
                      const content = (
                        <div className="flex h-28 flex-col items-center justify-center gap-2 rounded-2xl bg-white/95 p-4 ring-1 ring-white/10 transition hover:-translate-y-0.5">
                          {s.logo ? (
                            <img src={s.logo} alt={s.name || 'Auspiciador'} className="max-h-16 w-auto max-w-full object-contain" loading="lazy" />
                          ) : (
                            <span className="text-center text-sm font-bold text-slate-800">{s.name}</span>
                          )}
                          {s.logo && s.name && <span className="text-center text-[10px] font-semibold uppercase tracking-wide text-slate-500">{s.name}</span>}
                        </div>
                      );
                      return s.url ? (
                        <a key={i} href={s.url} target="_blank" rel="noopener noreferrer">{content}</a>
                      ) : (
                        <div key={i}>{content}</div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-16" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }}>
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="font-heading text-4xl font-extrabold">Tu crecimiento profesional empieza aquí</h2>
          <p className="mt-3 text-white/90">Suma esta certificación a tu perfil CNIB y marca la diferencia.</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            {buyUrl && (
              <a href={buyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold shadow-xl" style={{ color: accent }}>
                <Ticket className="h-4 w-4" /> {buyLabel}
              </a>
            )}
            {waUrl && t.cta_mode !== 'whatsapp' && (
              <a href={waUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border-2 border-white/80 px-6 py-3 text-sm font-bold text-white">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            )}
          </div>
        </div>
      </section>

      <footer className="py-8 text-center text-xs text-slate-500">
        Programa gestionado por CNIB — Cámara Nacional Inmobiliaria de Bolivia
      </footer>
    </div>
  );
}
